/**
 * <s-product-thumbnails> — PDP thumbnail rail.
 *
 * Thumbnail buttons use commandfor/command="--show" with data-index. The element
 * scrolls the matching slide of the gallery stage into view and keeps
 * aria-current on the active thumbnail in sync as the stage is swiped/scrolled.
 *
 * Required markup:
 *   <s-product-thumbnails id="..." data-stage="<stage id>">
 *     <button commandfor="<this id>" command="--show" data-index="0">...</button>
 *   </s-product-thumbnails>
 *   #<stage id> > [data-gallery-slide]
 */
class SProductThumbnails extends HTMLElement {
  #ac;
  #stage;
  #observer;

  connectedCallback() {
    this.#ac = new AbortController();
    this.#stage = document.getElementById(this.dataset.stage);
    if (!this.#stage) return;

    this.addEventListener('command', this.#handleCommand, { signal: this.#ac.signal });

    // Track the visible slide so the rail follows swipes, keyboard scrolling and zoom-close returns.
    this.#observer = new IntersectionObserver(this.#handleIntersect, { root: this.#stage, threshold: 0.6 });
    for (const slide of this.#slides()) this.#observer.observe(slide);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #slides() {
    return Array.from(this.#stage.querySelectorAll('[data-gallery-slide]'));
  }

  #handleCommand = (event) => {
    if (event.command !== '--show') return;
    const index = Number(event.source?.dataset.index ?? 0);
    const slide = this.#slides()[index];
    if (!slide) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // block: 'nearest' keeps the page from jumping; inline: 'start' follows reading direction (RTL-safe).
    slide.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
    this.#setActive(index);
  };

  #handleIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const index = this.#slides().indexOf(entry.target);
      if (index >= 0) this.#setActive(index);
    }
  };

  #setActive(index) {
    this.querySelectorAll('[data-index]').forEach((button) => {
      if (Number(button.dataset.index) === index) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('s-product-thumbnails')) {
  customElements.define('s-product-thumbnails', SProductThumbnails);
}
